import React, { useEffect, useState } from 'react';
import { Button, Skeleton, message, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CalendarOutlined, TagOutlined, RightOutlined, UnorderedListOutlined } from '@ant-design/icons';
import axios from '../../../config/axiosInstance';
import API_BASE_URL from '../../../config/api';
import { Helmet } from 'react-helmet-async';
import './news.css';

const MONTHS_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

// แปลงวันที่ ISO -> ไทย (พ.ศ.)
const formatThaiDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS_TH[d.getMonth()]} ${d.getFullYear() + 543}`;
};

// ตัด HTML + ย่อข้อความ (สำหรับ description/เนื้อหา)
const summarize = (html, len = 200) => {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > len ? `${text.slice(0, len)}…` : text;
};

const NewsFeedPage = () => {
  const navigate = useNavigate();
  const [newsList, setNewsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // null = ทั้งหมด
  const [loading, setLoading] = useState(true);

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/news`);
      setNewsList(res.data || []);
    } catch {
      message.error('โหลดข่าวล้มเหลว');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/news-categories`);
      setCategories(res.data || []);
    } catch {
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, []);

  // กรองข่าวตามหมวดหมู่ที่เลือก
  const visibleNews = selectedCategory
    ? newsList.filter((n) => Number(n.category_id) === Number(selectedCategory))
    : newsList;

  // นับจำนวนข่าวต่อหมวดหมู่
  const countByCategory = (catId) =>
    newsList.filter((n) => Number(n.category_id) === Number(catId)).length;

  return (
    <div className="news-page">
      <Helmet>
        <title>ข่าวสาร | สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)</title>
        <meta name="description" content="ข่าวสาร ประชาสัมพันธ์ และบทความของคณะสงฆ์ภาค 4-5 (ธรรมยุต)" />
      </Helmet>

      <div className="news-page-header">
        <h1 className="news-page-title">ข่าวสาร</h1>
        <p className="news-page-subtitle">
          ข่าวสาร ประชาสัมพันธ์ และบทความของคณะสงฆ์ภาค ๔-๕ (ธรรมยุต)
        </p>
      </div>

      <div className="news-layout">
        {/* Side Menu หมวดหมู่ */}
        <aside className="news-sidebar">
          <div className="news-sidebar-head">
            <UnorderedListOutlined /> หมวดหมู่
          </div>
          <ul className="news-cat-list">
            <li
              className={`news-cat-item${selectedCategory === null ? ' active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              <span>ทั้งหมด</span>
              <span className="news-cat-count">{newsList.length}</span>
            </li>
            {categories.map((cat) => (
              <li
                key={cat.id}
                className={`news-cat-item${Number(selectedCategory) === Number(cat.id) ? ' active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                <span>{cat.name}</span>
                <span className="news-cat-count">{countByCategory(cat.id)}</span>
              </li>
            ))}
          </ul>
        </aside>

        {/* รายการข่าว */}
        <div className="news-main">
          {loading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : visibleNews.length === 0 ? (
            <div className="news-empty-box">ยังไม่มีข่าวสารในหมวดหมู่นี้</div>
          ) : (
            <div className="news-list">
              {visibleNews.map((news) => (
                <article key={news.id} className="news-card">
                  <div
                    className="news-card-cover"
                    onClick={() => navigate(`/news/${news.id}`)}
                  >
                    {news.cover_image ? (
                      <img src={news.cover_image} alt={news.title} loading="lazy" />
                    ) : (
                      <div className="news-card-cover-placeholder">📰</div>
                    )}
                  </div>

                  <div className="news-card-body">
                    <div className="news-card-meta">
                      {news.category_name && (
                        <Tag color="gold" icon={<TagOutlined />} className="news-tag">
                          {news.category_name}
                        </Tag>
                      )}
                      {news.published_at && (
                        <span className="news-date">
                          <CalendarOutlined /> {formatThaiDate(news.published_at)}
                        </span>
                      )}
                    </div>

                    <h2
                      className="news-card-title"
                      onClick={() => navigate(`/news/${news.id}`)}
                    >
                      {news.title}
                    </h2>

                    <p className="news-card-desc">
                      {summarize(news.description || news.content, 200)}
                    </p>

                    <Button
                      type="primary"
                      className="news-read-more"
                      onClick={() => navigate(`/news/${news.id}`)}
                    >
                      อ่านต่อ <RightOutlined />
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsFeedPage;

